/**
 * PaperDetailsModal Component
 * 
 * Modal component for displaying detailed information about a paper submission.
 * Shows all paper details including authors, abstract, research areas, files, and conference details.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.paper - Paper object to display
 * @param {Object} props.conference - Conference object associated with the paper
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onHide - Function to call when modal is closed
 */

import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import api from '../../services/api';

const PaperDetailsModal = ({ paper, conference, show, onHide }) => {
    const [downloading, setDownloading] = useState(null);
    const LOCALE = 'en-GB';
    const DATE_FORMAT_OPTIONS = { timeZone: 'Etc/GMT' };

    if (!paper) return null;
    const conferenceYear = new Date(conference.startDate).getFullYear().toString().substring(2,5);

    /**
     * Handle file download with authentication
     */
    const handleFileDownload = async (fileType) => {
        try {
            setDownloading(fileType);
            const response = await api.get(`/papers/${paper._id}/download/${fileType}`, {
                responseType: 'blob',
            });

            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Set filename from response headers or use a default
            const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') 
                || paper.files[fileType]?.originalName 
                || `${fileType}_${paper._id}.pdf`;
                
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error downloading file:', error);
            alert('Failed to download file. Please try again.');
        } finally {
            setDownloading(null);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'under_review': { label: 'Under Review', className: 'bg-status-under-review' },
            'accepted': { label: 'Accepted', className: 'bg-status-accepted' },
            'rejected': { label: 'Rejected', className: 'bg-status-rejected' },
            'incomplete': { label: 'Update-Revise', className: 'bg-status-incomplete' },
            'withdrawn': { label: 'Withdrawn', className: 'bg-status-withdrawn' }
        };

        
        const config = statusConfig[status] || statusConfig['under_review'];
        return (
            <span className={`badge ${config.className} text-white`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString, showYear = true) => {
        if (!dateString) return 'Not specified';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';
            
            const options = { 
                year: showYear ? 'numeric' : undefined,
                month: 'long', 
                day: 'numeric' 
            };
            return date.toLocaleDateString(LOCALE, { ...options, ...DATE_FORMAT_OPTIONS });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid date';
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Paper Details
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <div className="row">
                    {/* Paper Information */}
                    <div className="col-12 mb-4">
                        <h5 className="border-bottom pb-2">Paper Information</h5>
                        <div className="row">
                            <div className="col-md-6">
                                <strong>Title:</strong>
                                <p className="mt-1">{paper.manuscriptTitle}</p>
                            </div>
                            <div className="col-md-3">
                                <strong>Paper Number:</strong>
                                <p className="mt-1">
                                    <span className="badge bg-primary">
                                        MS{conferenceYear}~{paper.paperNumber+101 ?? 'N/A'}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-3">
                                <strong>Status:</strong>
                                <div className="mt-1">
                                    {getStatusBadge(paper.status)}
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-3">
                            <strong>Abstract:</strong>
                            <p className="mt-1 text-justify">{paper.manuscriptAbstract}</p>
                        </div>
                        
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <strong>Submission Date:</strong>
                                <p className="mt-1">{new Date(paper.submissionDate).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Research Areas:</strong>
                                <p className="mt-1">
                                    {paper.researchAreas?.join(', ') || 'Not specified'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Authors Information */}
                    <div className="col-12 mb-4">
                        <h5 className="border-bottom pb-2">Authors</h5>
                        {paper.authors && paper.authors.length > 0 ? (
                            <div className="row">
                                {paper.authors.map((author, index) => (
                                    <div key={index} className="col-md-6 mb-3">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <h6 className="card-title">
                                                    {index === 0 && (
                                                        <span className="badge bg-primary me-2">Primary</span>
                                                    )}
                                                    {author.firstName} {author.lastName}
                                                </h6>
                                                <p className="card-text">
                                                    <small className="text-muted">
                                                        <i className="bi bi-envelope me-1"></i>
                                                        {author.email}
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">
                                                        <i className="bi bi-building me-1"></i>
                                                        {author.affiliation || 'Not provided'}
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        {author.country || 'Not provided'}
                                                    </small>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted">No authors information available</p>
                        )}
                    </div>

                    {/* Admin Notes */}
                    {paper.adminNotes && (
                        <div className="col-12 mb-4">
                            <h5 className="border-bottom pb-2">
                                <i className="bi bi-clipboard-check me-2"></i>
                                Admin Notes
                            </h5>
                            <div className="alert alert-secondary">
                                <i className="bi bi-lock-fill me-2"></i>
                                <small className="text-muted d-block mb-2">Internal notes (not visible to authors)</small>
                                <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {paper.adminNotes}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Files Information */}
                    <div className="col-12">
                        <h5 className="border-bottom pb-2">Uploaded Files</h5>
                        <div className="row">
                            <div className="col-md-6">
                                <strong>Full Manuscript:</strong>
                                {paper.files?.fullManuscript ? (
                                    <div className="mt-1">
                                        <button
                                            onClick={() => handleFileDownload('fullManuscript')}
                                            className="btn btn-sm btn-outline-primary"
                                            disabled={downloading === 'fullManuscript'}
                                        >
                                            {downloading === 'fullManuscript' ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-download me-1"></i>
                                                    {paper.files.fullManuscript.originalName}
                                                </>
                                            )}
                                        </button>
                                        <small className="d-block text-muted mt-1">
                                            Size: {Math.round(paper.files.fullManuscript.size / 1024)} KB
                                        </small>
                                    </div>
                                ) : (
                                    <p className="text-muted mt-1">Not provided</p>
                                )}
                            </div>
                            
                            <div className="col-md-6">
                                <strong>Blind Copy:</strong>
                                {paper.files?.blindCopyManuscript ? (
                                    <div className="mt-1">
                                        <button
                                            onClick={() => handleFileDownload('blindCopyManuscript')}
                                            className="btn btn-sm btn-outline-secondary"
                                            disabled={downloading === 'blindCopyManuscript'}
                                        >
                                            {downloading === 'blindCopyManuscript' ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-download me-1"></i>
                                                    {paper.files.blindCopyManuscript.originalName}
                                                </>
                                            )}
                                        </button>
                                        <small className="d-block text-muted mt-1">
                                            Size: {Math.round(paper.files.blindCopyManuscript.size / 1024)} KB
                                        </small>
                                    </div>
                                ) : (
                                    <p className="text-muted mt-1">Not provided</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <div>
                        {paper.authors?.[0]?.email && (
                            <a
                                href={`mailto:${paper.authors[0].email}?subject=Regarding your paper: ${paper.manuscriptTitle}`}
                                className="btn btn-outline-primary"
                            >
                                <i className="bi bi-envelope me-2"></i>
                                Contact Primary Author
                            </a>
                        )}
                    </div>
                    <button type="button" className="btn btn-secondary" onClick={onHide}>
                        Close
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default PaperDetailsModal;